import { createAsyncThunk } from "@reduxjs/toolkit";
import { HttpService } from "../../modules/shared/services/http.service";
import { GlobalService } from "../../modules/shared/services/global.service";
import { hideLoader, showLoader } from "../Slices/loadingSlice";
import i18n from "../../i18n";

// Interfaces for Admin Professional Scoring Data
export interface AdminPerformanceMetricsData {
  averageRating: number;
  responseRate: number;
  completionRate: number;
  topPerformers: number;
  trendData: Array<{
    month: string;
    rating: number;
    completion: number;
    response: number;
  }>;
  professionals: Array<{
    id: string;
    name: string;
    profession: string;
    metrics: {
      rating: number;
      completionRate: number;
      responseTime: string;
      totalJobs: number;
      emergencyJobs: number;
      onTimeRate: number;
      satisfactionScore: number;
      revenueGenerated: string;
    };
    trends: {
      rating: string;
      jobs: string;
      revenue: string;
    };
    badges: string[];
    status: string;
  }>;
  totalCount: number;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
}

export interface AdminRankingsData {
  topPerformers: number;
  risingStars: number;
  categories: number;
  regions: number;
  rankings: Array<{
    id: string;
    name: string;
    profession: string;
    region: string;
    rank: {
      overall: number;
      category: number;
      region: number;
      trend: string;
    };
    metrics: {
      rating: number;
      totalJobs: number;
      completionRate: number;
      responseTime: string;
      revenue: string;
      customerSatisfaction: number;
    };
    badges: string[];
    achievements: Array<{
      type: string;
      label: string;
    }>;
    performance: {
      thisMonth: {
        jobs: number;
        revenue: string;
        rating: number;
      };
      trend: {
        jobs: string;
        revenue: string;
        rating: string;
      };
    };
    performanceScore: number;
  }>;
  totalCount: number;
  hasNextPage: boolean;
  currentPage: number;
  totalPages: number;
}

export interface AdminScoringSettingsData {
  scoringCategories: Array<{
    id: number;
    name: string;
    icon: string;
    weight: number;
    metrics: Array<{
      name: string;
      type: string;
      points: any;
      conditions?: string[];
      thresholds?: any;
    }>;
    bonuses?: Array<{
      name: string;
      points: number;
      condition: string;
    }>;
    penalties?: Array<{
      name: string;
      points: number;
      condition: string;
    }>;
  }>;
  videoAccessSettings: {
    minimumTurnover: Array<{
      category: string;
      threshold: number;
      enabled: boolean;
    }>;
    mandatoryRecording: Array<{
      category?: string;
      professionalId?: string;
      name?: string;
      enabled: boolean;
      autoTrigger: boolean;
    }>;
    autoTurnoverRule: {
      enabled: boolean;
      defaultThreshold: number;
    };
    autoRecordingRule: {
      enabled: boolean;
      callsWithoutContract: number;
    };
  };
  performanceTiers: Array<{
    name: string;
    score: string;
    color: string;
  }>;
}

// Admin Professional Scoring Actions
export const getAdminPerformanceMetrics = createAsyncThunk<
  AdminPerformanceMetricsData,
  { page?: number; limit?: number; search?: string; category?: string; months?: number } | undefined,
  { rejectValue: string }
>("adminProfessionalScoring/getPerformanceMetrics", async (params = {}, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    
    // Build query string
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.category) queryParams.append("category", params.category);
    if (params.months) queryParams.append("months", params.months.toString());
    
    const url = `api/v1/users/admin/performance-metrics${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await HttpService.get(url, true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.metricsFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.metricsFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch performance metrics"
    );
  }
});

export const getAdminRankings = createAsyncThunk<
  AdminRankingsData,
  { page?: number; limit?: number; search?: string; timeframe?: string } | undefined,
  { rejectValue: string }
>("adminProfessionalScoring/getRankings", async (params = {}, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    
    // Build query string
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.search) queryParams.append("search", params.search);
    if (params.timeframe) queryParams.append("timeframe", params.timeframe);
    
    const url = `api/v1/users/admin/rankings${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;
    const response = await HttpService.get(url, true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.rankingsFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.rankingsFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch rankings"
    );
  }
});

export const getAdminScoringSettings = createAsyncThunk<
  AdminScoringSettingsData,
  void,
  { rejectValue: string }
>("adminProfessionalScoring/getScoringSettings", async (_, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.get("api/v1/users/admin/scoring-settings", true);
    dispatch(hideLoader());
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.settingsFetchFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.settingsFetchFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to fetch scoring settings"
    );
  }
});

export const createScoringCategory = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>("adminProfessionalScoring/createCategory", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.post("api/v1/users/admin/scoring-categories", data, true);
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.scoring.categoryCreated"),
      i18n.t("toast.scoring.categoryCreatedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.categoryCreateFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.categoryCreateFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to create category"
    );
  }
});

export const updateScoringCategory = createAsyncThunk<
  any,
  { id: string; data: any },
  { rejectValue: string }
>("adminProfessionalScoring/updateCategory", async ({ id, data }, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.patch(`api/v1/users/admin/scoring-categories/${id}`, data, true);
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.scoring.categoryUpdated"),
      i18n.t("toast.scoring.categoryUpdatedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.categoryUpdateFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.categoryUpdateFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to update category"
    );
  }
});

export const deleteScoringCategory = createAsyncThunk<
  any,
  string,
  { rejectValue: string }
>("adminProfessionalScoring/deleteCategory", async (id, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.deleteRecord(`api/v1/users/admin/scoring-categories/${id}`, true);
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.scoring.categoryDeleted"),
      i18n.t("toast.scoring.categoryDeletedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.categoryDeleteFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.categoryDeleteFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to delete category"
    );
  }
});

export const saveVideoAccessSettings = createAsyncThunk<
  any,
  any,
  { rejectValue: string }
>("adminProfessionalScoring/saveVideoSettings", async (data, { rejectWithValue, dispatch }) => {
  try {
    dispatch(showLoader());
    const response = await HttpService.post("api/v1/users/admin/video-access-settings", data, true);
    dispatch(hideLoader());
    GlobalService.showToast(
      "success",
      i18n.t("toast.scoring.videoAccessSettingsSaved"),
      i18n.t("toast.scoring.videoAccessSettingsSavedMessage")
    );
    return response;
  } catch (error: any) {
    dispatch(hideLoader());
    GlobalService.showToast(
      "error",
      i18n.t("toast.scoring.videoAccessSettingsSaveFailed"),
      error?.response?.data?.message || i18n.t("toast.scoring.videoAccessSettingsSaveFailedMessage")
    );
    return rejectWithValue(
      error?.response?.data?.message || "Failed to save settings"
    );
  }
});
